import type { ResumoFuncionario } from './FuncionarioResumoCard';
import { useProgressoes } from '@/hooks/useProgressoes';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { aplicaRegraEspecial18Anos, PONTOS_REGRA_18_ANOS, calcularAnosServico } from '@/utils/progressaoUtils';

function calcularAnosServicoLocal(dataEntrada?: string): number | null {
  if (!dataEntrada) return null;
  const anos = calcularAnosServico(dataEntrada);
  return anos > 0 ? anos : null;
}

interface ResumoDetalheModalProps {
  resumo: ResumoFuncionario;
  onClose: () => void;
}

const avaliacaoConfig: Record<string, { bg: string; text: string; dot: string }> = {
  Excelente: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  Bom: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  Regular: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400' },
  Inadequado: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  Relevante: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
};

const avatarColors = [
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-teal-100 text-teal-700',
  'bg-sky-100 text-sky-700',
  'bg-orange-100 text-orange-700',
];

function getInitials(nome: string) {
  const parts = nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1);
  return parts.map((n) => n[0]).join('');
}

function getAvatarColor(id: string) {
  const idx = parseInt(id.replace(/\D/g, ''), 10) % avatarColors.length;
  return avatarColors[idx];
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

export default function ResumoDetalheModal({ resumo, onClose }: ResumoDetalheModalProps) {
  const { animating, handleClose } = useModalAnimation(onClose);
  const { progressoes, loading: loadingProg } = useProgressoes(resumo.id);
  const { funcionarios } = useFuncionarios();
  const funcionario = funcionarios.find((f) => f.id === resumo.id);
  const anosServico = calcularAnosServicoLocal(funcionario?.dataEntradaFuncaoPublica);
  const pontosAtuaisCicloCalc = resumo.pontosAtuaisCiclo ?? resumo.pontosAcumulados;
  const jaProgrediu = resumo.pontosDescontados > 0;
  const regraEspecial = aplicaRegraEspecial18Anos(funcionario?.dataEntradaFuncaoPublica, pontosAtuaisCicloCalc, jaProgrediu, 2024);
  const sorted = [...resumo.registos].sort((a, b) => a.ano - b.ano);
  const colorClass = getAvatarColor(resumo.id);
  const initials = getInitials(resumo.nome);

  const pontosDescontados = resumo.pontosDescontados ?? 0;
  const pontosAtuaisCiclo = resumo.pontosAtuaisCiclo ?? resumo.pontosAcumulados;

  // Acumulado progressivo (bruto, sem desconto — para mostrar o histórico real)
  let acumulado = 0;
  const registosComAcumulado = sorted.map((r) => {
    acumulado += r.pontos;
    return { ...r, acumulado };
  });

  const maxAcumulado = Math.max(acumulado, 1);

  // Verificar se há progressões guardadas na tabela (fonte de verdade para datas)
  // Se não houver (dados antigos), cai back para o campo pontosDescontados do funcionário
  const temProgressoesDetalhadas = progressoes.length > 0;
  const totalDescontadoProgressoes = progressoes.reduce((s, p) => s + p.pontosDescontados, 0);

  // Para dados antigos sem registo na tabela progressoes, mostramos uma linha genérica
  const mostrarLinhaGenerica = pontosDescontados > 0 && !loadingProg && !temProgressoesDetalhadas;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/40 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-4 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className={`w-12 h-12 flex items-center justify-center rounded-full text-base font-bold flex-shrink-0 ${colorClass}`}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-800 text-lg leading-tight truncate">{resumo.nome}</h2>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <p className="text-sm text-slate-400">{resumo.carreira} · Nº {resumo.id}</p>
              {anosServico !== null && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                  <i className="ri-time-line text-xs" />
                  {anosServico} anos de serviço
                </span>
              )}
              {regraEspecial && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full cursor-help"
                  title="18+ anos de serviço a 30/08/2023 — data de referência legal para esta regra especial"
                >
                  <i className="ri-award-fill text-xs" />
                  Regra especial 2024 · 18 anos a 30/08/2023 · limiar {PONTOS_REGRA_18_ANOS} pts
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="whitespace-nowrap w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-6">
          {/* Gráfico de progressão acumulada */}
          {registosComAcumulado.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-line-chart-line text-base text-slate-500" /></div>
                Progressão de Pontos Acumulados
              </h3>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-end gap-2 h-24">
                  {registosComAcumulado.map((r) => {
                    const height = Math.max((r.acumulado / maxAcumulado) * 100, 5);
                    const aval = avaliacaoConfig[r.avaliacao] ?? avaliacaoConfig['Bom'];
                    return (
                      <div key={`${r.ano}-${r.fonte}`} className="flex-1 flex flex-col items-center gap-1 group/bar" title={`${r.ano}: ${r.acumulado.toFixed(1)} pts acumulados`}>
                        <div
                          className={`w-full rounded-t-md transition-all ${aval.bg} border-t-2 ${aval.dot.replace('bg-', 'border-')}`}
                          style={{ height: `${height}%` }}
                        />
                        <span className="text-xs text-slate-400 whitespace-nowrap">{r.ano}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-400">0 pts</span>
                  <span className="text-xs text-slate-500 font-medium">{maxAcumulado.toFixed(1)} pts acumulados</span>
                </div>
              </div>
            </div>
          )}

          {/* Tabela de registos */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-table-line text-base text-slate-500" /></div>
              Detalhe por Ano
            </h3>
            {registosComAcumulado.length === 0 && !pontosDescontados ? (
              <div className="bg-slate-50 rounded-xl py-8 flex flex-col items-center gap-2 text-slate-400">
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-inbox-line text-2xl" /></div>
                <p className="text-sm">Sem avaliações registadas</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-100">
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Ano / Data</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Avaliação</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Pts Chefe</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Pts CCA</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Pontos</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Acumulado</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">Fonte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registosComAcumulado.map((r, i) => {
                      const aval = avaliacaoConfig[r.avaliacao] ?? avaliacaoConfig['Bom'];
                      const isLast = i === registosComAcumulado.length - 1 && pontosDescontados === 0;
                      return (
                        <tr key={`${r.ano}-${r.fonte}`} className={`border-b border-slate-50 ${isLast ? 'bg-emerald-50/30' : 'hover:bg-slate-50'} transition-colors`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              {isLast && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">Último</span>}
                              <span className="font-semibold text-slate-700 text-sm">{r.ano}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${aval.bg} ${aval.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${aval.dot}`} />
                              {r.avaliacao}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{r.pontuacaoChefe.toFixed(1)}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{r.pontuacaoCCA.toFixed(1)}</td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-slate-800">{r.pontos % 1 === 0 ? r.pontos.toFixed(0) : r.pontos.toFixed(1)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-slate-500 rounded-full" style={{ width: `${(r.acumulado / maxAcumulado) * 100}%` }} />
                              </div>
                              <span className="text-sm font-bold text-slate-700">{r.acumulado.toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {r.fonte === 'siadap' ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                                SIADAP
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                                Manual
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Linhas de progressão detalhadas (uma por progressão registada) */}
                    {!loadingProg && temProgressoesDetalhadas && progressoes.map((p, idx) => {
                      const acumuladoAposProgressao = acumulado - progressoes.slice(idx + 1).reduce((s, pp) => s + pp.pontosDescontados, 0) - p.pontosDescontados;
                      return (
                        <tr key={p.id} className="border-b border-slate-100 bg-rose-50/40">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 flex-shrink-0">
                                <i className="ri-arrow-up-circle-line text-xs" />
                              </div>
                              <div>
                                <span className="text-xs font-semibold text-rose-700 block">Progressão</span>
                                <span className="text-xs text-slate-400">{formatDate(p.dataProgressao)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                              Nível {p.nivelAnterior} → {p.nivelNovo}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400" colSpan={2}>
                            {p.observacoes ? (
                              <span className="italic text-slate-500 truncate max-w-[120px] block">{p.observacoes}</span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-rose-600">
                              -{p.pontosDescontados % 1 === 0 ? p.pontosDescontados.toFixed(0) : p.pontosDescontados.toFixed(1)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-emerald-500 rounded-full"
                                  style={{ width: `${Math.min(100, Math.max(0, (acumuladoAposProgressao / Math.max(acumulado, 1)) * 100))}%` }}
                                />
                              </div>
                              <span className="text-sm font-bold text-emerald-700">
                                {acumuladoAposProgressao % 1 === 0 ? acumuladoAposProgressao.toFixed(0) : acumuladoAposProgressao.toFixed(1)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-100">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 inline-block" />
                              Progressão
                            </span>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Linha genérica para dados antigos sem registo detalhado */}
                    {mostrarLinhaGenerica && (
                      <tr className="border-b border-slate-100 bg-rose-50/40">
                        <td className="px-4 py-3" colSpan={4}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 flex-shrink-0">
                              <i className="ri-arrow-up-circle-line text-xs" />
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-rose-700">Progressão registada</span>
                              <span className="text-xs text-slate-400 ml-2">Pontos consumidos pela progressão de nível</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-rose-600">-{pontosDescontados % 1 === 0 ? pontosDescontados.toFixed(0) : pontosDescontados.toFixed(1)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, (pontosAtuaisCiclo / Math.max(acumulado, 1)) * 100)}%` }} />
                            </div>
                            <span className="text-sm font-bold text-emerald-700">{pontosAtuaisCiclo % 1 === 0 ? pontosAtuaisCiclo.toFixed(0) : pontosAtuaisCiclo.toFixed(1)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-100">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 inline-block" />
                            Progressão
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {/* Rodapé com total líquido */}
                  {(temProgressoesDetalhadas || mostrarLinhaGenerica) && (
                    <tfoot>
                      <tr className="bg-slate-50 border-t-2 border-slate-200">
                        <td colSpan={4} className="px-4 py-3">
                          <span className="text-xs font-semibold text-slate-600">Total líquido do ciclo atual</span>
                          {temProgressoesDetalhadas && (
                            <span className="text-xs text-slate-400 ml-2">
                              ({progressoes.length} progressão{progressoes.length !== 1 ? 'ões' : ''} · -{totalDescontadoProgressoes} pts descontados)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {/* bruto = soma dos pontos das avaliações (sem pontosAtuais manuais, sem descontos) */}
                          <span className="text-sm font-bold text-slate-800">
                            {acumulado % 1 === 0 ? acumulado.toFixed(0) : acumulado.toFixed(1)} - {totalDescontadoProgressoes % 1 === 0 ? totalDescontadoProgressoes.toFixed(0) : totalDescontadoProgressoes.toFixed(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3" colSpan={2}>
                          {/* pontosAtuaisCiclo já é calculado corretamente pelo calcularPontosCiclo */}
                          <span className="text-sm font-bold text-emerald-700">
                            = {pontosAtuaisCiclo % 1 === 0 ? pontosAtuaisCiclo.toFixed(0) : pontosAtuaisCiclo.toFixed(1)} pts restantes
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
